<template>
  <div class="explore-bar">
    <v-combobox
      ref="combobox"
      v-model="keywordsTemp"
      class="explore-combobox"
      :class="{ 'has-error': !valid }"
      @clear="clearResearch"
      @change="searchByKeywords"
      :search-input.sync="internalSearch"
      enterkeyhint="search"
      @keydown.native.enter="onSearchKey"
      autofocus
      multiple
      clearable
      solo
    >
      <template v-slot:selection="{ item, index }">
        <BaseChip
          class="explore-bar__chip"
          size="sm"
          preset="soft"
          tone="info"
          clearable
          :interactive="false"
          :clear-label="$t('actions.delete')"
          @clear="removeKeyword(index)"
        >
          {{ item }}
        </BaseChip>
      </template>
      <template v-slot:prepend-inner>
        <BaseIcon name="search" class="explore-bar__icon" />
      </template>
      <template v-slot:append>
        <BaseShortcut
          value="~"
          isActionable
          @shortcut-clicked="addTilde"
          v-tooltip="{ content: $t('search.tild'), placement: 'top', delay: 500 }"
        />
        <v-popover v-if="valid" placement="bottom" trigger="hover" popoverClass="tender-bottom-nav__nav-tooltip">
          <BaseIcon name="info" class="explore-bar__icon info" />
          <template v-slot:popover>
            <div class="explore-bar__info">
              <i18n path="page.explore.info">
                <template v-slot:page>
                  <BaseLink to="/keywords" class="explore-bar__link">{{ $t('menu.keywords') }}</BaseLink>
                </template>
              </i18n>
            </div>
          </template>
        </v-popover>
        <v-popover v-else placement="bottom" trigger="hover" popoverClass="tender-bottom-nav__nav-tooltip">
          <BaseIcon name="info" class="explore-bar__icon alert" />
          <template v-slot:popover>
            <KeywordsErrors :validation="validation" />
          </template>
        </v-popover>
      </template>
      <template v-slot:label>
        <span class="explore-bar__label">{{ label }}</span>
      </template>
    </v-combobox>
  </div>
</template>

<script>
import BaseLink from '@/components/base/BaseLink';
import BaseChip from '@/components/base/BaseChip.vue';
import BaseIcon from '@/components/base/BaseIcon';
import KeywordsErrors from '@/components/util/KeywordsErrors';
import BaseShortcut from '../base/BaseShortcut.vue';

import { bus } from '@/main';
import { validateKeywords } from '@/util/keywords-util';
import { mapActions, mapGetters } from 'vuex';
import { viewport } from '@/util/viewport';
import { closeKeyboardOnTouch } from '@/util/search-key';

export default {
  name: 'ExploreBar',
  components: { BaseLink, BaseChip, BaseIcon, KeywordsErrors, BaseShortcut },
  data() {
    return {
      keywordsTemp: [],
      validation: null,
      internalSearch: '',
    };
  },
  computed: {
    ...mapGetters('exploreSearchFilters', ['keywords']),
    // Phones: shorter, beside the filters button.
    label() {
      return viewport.size === 'phone'
        ? this.$t('page.tenders.views.search-placeholder-short')
        : this.$t('page.explore.search-bar');
    },
    valid() {
      return this.validation ? this.validation.valid : true;
    },
  },
  methods: {
    validateKeywords,
    ...mapActions('exploreSearchFilters', ['setKeywords']),
    clearResearch() {
      this.setKeywords({ keywords: [] });
      bus.$emit('refresh-explore-page');
    },
    validateAllKeywords() {
      this.validation = validateKeywords(this.keywordsTemp);
    },
    searchByKeywords() {
      this.validateAllKeywords();
      if (this.valid) {
        this.setKeywords({ keywords: this.keywordsTemp });
        bus.$emit('refresh-explore-page');
        this.$emit('search-clicked');
      }
    },
    // After the field's own Enter, which makes the typed word a chip and searches on the next tick;
    // a word the search refused keeps the keyboard, to be fixed.
    onSearchKey() {
      this.$nextTick(() => {
        if (this.valid) closeKeyboardOnTouch(this.$refs.combobox);
      });
    },
    removeKeyword(index) {
      this.keywordsTemp.splice(index, 1);
      this.searchByKeywords();
    },
    addTilde() {
      this.$nextTick(() => {
        const inputEl = this.$refs.combobox.$el.querySelector('input');
        if (!inputEl) return;

        const currentValue = this.internalSearch || '';
        this.internalSearch = currentValue + '~';

        this.$nextTick(() => {
          inputEl.focus();
        });
      });
    },
    focusInput() {
      this.$nextTick(() => {
        if (this.$refs.combobox && this.$refs.combobox.$el) {
          const inputEl = this.$refs.combobox.$el.querySelector('input');
          if (inputEl) inputEl.focus();
        }
      });
    },
  },
  mounted() {
    this.keywordsTemp = this.keywords;
    this.focusInput();
  },
  activated() {
    this.focusInput();
  },
};
</script>

<style lang="scss">
.explore-bar {
  flex: 1;

  &__icon {
    color: $color-normal-pale;
    margin: 0 8px;
    width: 20px;

    &.info {
      color: $color-normal-pale;
    }

    &.alert {
      color: $color-error !important;
    }
  }

  &__info {
    line-height: 16px;
  }

  &__link {
    display: inline;
  }

  &__label {
    color: $color-neutral;
  }

  &__chip {
    margin: 3px;
  }

  .explore-combobox {
    border-radius: $border-radius-root;

    &.has-error {
      .v-input__slot {
        border: 1px solid $color-error;
      }
    }

    &:focus-within {
      .v-input__slot {
        border-color: $color-accent;
        box-shadow: 0 0 0 3px rgba($color-accent-500, 0.1) !important;
      }

      .v-input__prepend-inner .explore-bar__icon {
        color: $color-accent;
      }
    }

    .v-input__slot {
      min-height: 52.8px !important;

      // Phones: as tall as the filters button beside it.
      @include screen-below('tablet') {
        min-height: 40px !important;
      }

      margin: 0px;
      padding: 0 12px 0 8px !important;
      background: $color-slate-50 !important;
      border: 1px solid $color-border;
      box-shadow: none !important;
      transition: border 200ms ease, box-shadow 200ms ease !important;
    }

    input {
      font-size: 16px;
    }

    // The placeholder here is a Vuetify floating .v-label (not an input placeholder), so match the
    // muted search-bar placeholder on it. Vuetify still turns it accent while focused.
    .v-label {
      color: $color-text-muted;
      font-size: 16px;

      // Phones: it stops before the buttons at the end of the field ("~", info), cut short with
      // "…", as the contact directory's search does.
      @include screen-below('tablet') {
        max-width: calc(100% - 96px);
      }
    }

    .has-error {
      border: 1px solid $color-error;
    }

    .v-text-field__details {
      display: none;
    }

    .v-input__icon--append {
      display: none;
    }
  }
}
// Prevent the tooltip to disappear too quickly
.tooltip[x-placement^='top'] {
  margin-bottom: 5px;
}

.tooltip[x-placement^='bottom'] {
  margin-top: 5px;
}

.tooltip[x-placement^='right'] {
  margin-left: 5px;
}

.tooltip[x-placement^='left'] {
  margin-right: 5px;
}
</style>
